import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { Check, ArrowRight, Heart, Flame, Sparkles, Zap, Crown } from 'lucide-react';

const PricingPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const features = [
    { name: language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swiping', free: false, premium: true },
    { name: language === 'nl' ? 'Woningen bekijken' : 'View properties', free: true, premium: true },
    { name: language === 'nl' ? 'Automatische berichten' : 'Automatic messages', free: false, premium: true },
    { name: language === 'nl' ? 'Alle zoekfilters' : 'All search filters', free: false, premium: true },
    { name: language === 'nl' ? 'Profiel boost' : 'Profile boost', free: false, premium: true },
    { name: language === 'nl' ? 'Match notificaties' : 'Match notifications', free: false, premium: true },
    { name: language === 'nl' ? 'Verhuurders contacteren' : 'Contact landlords', free: false, premium: true },
    { name: language === 'nl' ? 'Direct opzegbaar' : 'Cancel anytime', free: '-', premium: true },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-black" data-testid="pricing-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative py-24 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-rose-600 via-pink-600 to-red-700"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-500/30 via-transparent to-transparent"></div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-5 py-2.5 mb-6">
              <Flame className="w-5 h-5 text-yellow-400" />
              <span className="text-white font-semibold text-sm tracking-wide">TARIEVEN</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
              {language === 'nl' ? 'Simpel.' : 'Simple.'}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400">
                {' '}{language === 'nl' ? 'Transparant.' : 'Transparent.'}
              </span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Eén abonnement met alles wat je nodig hebt. Geen verborgen kosten.'
                : 'One subscription with everything you need. No hidden costs.'}
            </p>
          </div>
        </section>

        {/* Pricing Card */}
        <section className="py-20 px-4 bg-black">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Free Plan */}
              <Card className="border border-gray-800 bg-gray-900">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {language === 'nl' ? 'Gratis' : 'Free'}
                  </h3>
                  <p className="text-gray-400 text-sm mb-6">
                    {language === 'nl' ? 'Bekijk het aanbod' : 'View the listings'}
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-black text-white">€0</span>
                    <span className="text-gray-500 ml-2">{language === 'nl' ? 'voor altijd' : 'forever'}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        {feature.free === true ? (
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-800 flex-shrink-0" />
                        )}
                        <span className={feature.free ? 'text-gray-300' : 'text-gray-600'}>{feature.name}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    variant="outline" 
                    className="w-full h-12 border-gray-700 text-gray-300 hover:bg-gray-800"
                    onClick={() => navigate('/registreren')}
                  >
                    {language === 'nl' ? 'Gratis aanmelden' : 'Sign up free'}
                  </Button>
                </CardContent>
              </Card>

              {/* Premium Plan */}
              <Card className="border-2 border-rose-500 bg-gradient-to-br from-gray-900 to-gray-950 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-rose-500 to-pink-500 py-2 text-center">
                  <span className="text-white font-bold text-sm tracking-wider flex items-center justify-center gap-2">
                    <Crown className="w-4 h-4" />
                    {language === 'nl' ? 'MEEST GEKOZEN' : 'MOST POPULAR'}
                  </span>
                </div>
                <CardContent className="p-8 pt-14">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-6 h-6 text-rose-500" />
                    <h3 className="text-xl font-bold text-white">Premium</h3>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    {language === 'nl' ? 'Volledige toegang' : 'Full access'}
                  </p>
                  <div className="mb-6">
                    <span className="text-gray-500 line-through text-xl mr-2">€14,99</span>
                    <span className="text-5xl font-black text-white">€4,99</span>
                    <span className="text-gray-400 ml-2">{language === 'nl' ? '/week' : '/week'}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                        <span className="text-gray-300">{feature.name}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full h-14 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-bold text-lg"
                    onClick={() => navigate('/premium')}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {language === 'nl' ? 'Start Premium' : 'Start Premium'}
                  </Button>
                  <p className="text-center text-gray-500 text-sm mt-4">
                    {language === 'nl' ? '14 dagen geld-terug-garantie' : '14-day money-back guarantee'}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* For Landlords */}
        <section className="py-20 bg-gray-900 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-rose-500/20 text-rose-400 border-0 mb-4">VOOR VERHUURDERS</Badge>
            <h2 className="text-3xl font-black text-white mb-4">
              {language === 'nl' ? 'Gratis adverteren' : 'Advertise for free'}
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              {language === 'nl'
                ? 'Verhuurders kunnen gratis advertenties plaatsen op Swipe je woning. Ontvang direct reacties van gemotiveerde huurders die bij jouw woning passen.'
                : 'Landlords can post listings for free on Swipe je woning. Receive direct responses from motivated tenants who match your property.'}
            </p>
            <Button 
              onClick={() => navigate('/verhuren')}
              className="bg-white text-gray-900 hover:bg-gray-100 h-12 px-8 font-semibold"
            >
              {language === 'nl' ? 'Start met verhuren' : 'Start renting out'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
